import { useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  MultiSelect,
  NumberInput,
  Stack,
  Switch,
  Table,
  Tabs,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { unwrap } from '../api/client';
import {
  addKnowledge,
  createChannelAccount,
  deleteKnowledge,
  draftCalibration,
  draftMisses,
  getAgentSettings,
  listChannelAccounts,
  listKnowledge,
  setAgentDial,
  setAgentPersona,
  setChannelAccountEnabled,
} from '../api/gen';
import type { Settings } from '../api/gen';
import { useOrg } from '../api/org';
import { percent, when } from '../lib/format';

type Intent = Settings['autoSendIntents'][number];

// The closed set the drafting prompt names; the generated type keeps it
// honest, since a value the server does not take is a type error here.
const INTENTS: { value: Intent; label: string }[] = [
  { value: 'question', label: 'Question' },
  { value: 'request', label: 'Request' },
  { value: 'complaint', label: 'Complaint' },
  { value: 'order', label: 'Order' },
  { value: 'billing', label: 'Billing' },
  { value: 'waitlist', label: 'Waitlist' },
  { value: 'spam', label: 'Spam' },
  { value: 'other', label: 'Other' },
];

// The one place the agent is tuned: who it answers as, what it may know,
// whether it may send, and the evidence for deciding that last one.
export function SettingsPage() {
  return (
    <Stack>
      <Title order={3}>Settings</Title>
      <Tabs defaultValue="agent">
        <Tabs.List>
          <Tabs.Tab value="agent">Agent</Tabs.Tab>
          <Tabs.Tab value="knowledge">Knowledge</Tabs.Tab>
          <Tabs.Tab value="channels">Channels</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="agent" pt="md">
          <AgentTab />
        </Tabs.Panel>
        <Tabs.Panel value="knowledge" pt="md">
          <KnowledgeTab />
        </Tabs.Panel>
        <Tabs.Panel value="channels" pt="md">
          <ChannelsTab />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

function AgentTab() {
  const org = useOrg();
  const qc = useQueryClient();
  const [business, setBusiness] = useState('');
  const [instructions, setInstructions] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [threshold, setThreshold] = useState<number | string>(0.8);
  const [intents, setIntents] = useState<Intent[]>([]);

  const settings = useQuery({
    queryKey: ['agent-settings'],
    queryFn: () => unwrap(getAgentSettings({ headers: org })),
  });

  useEffect(() => {
    if (!settings.data) return;
    setBusiness(settings.data.businessName);
    setInstructions(settings.data.instructions);
    setEnabled(settings.data.autoSendEnabled);
    setThreshold(settings.data.confidenceThreshold);
    setIntents(settings.data.autoSendIntents);
  }, [settings.data]);

  const savePersona = useMutation({
    mutationFn: () =>
      unwrap(setAgentPersona({ headers: org, body: { businessName: business, instructions } })),
    onSuccess: () => {
      notifications.show({ message: 'Saved.' });
      void qc.invalidateQueries({ queryKey: ['agent-settings'] });
    },
    onError: (e) => notifications.show({ color: 'red', message: (e as Error).message }),
  });

  const saveDial = useMutation({
    mutationFn: () =>
      unwrap(
        setAgentDial({
          headers: org,
          // The allowlist is always sent, so what is saved is what is on
          // screen; the server keeps the stored one only when it is omitted.
          body: {
            autoSendEnabled: enabled,
            confidenceThreshold: Number(threshold),
            autoSendIntents: intents,
          },
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['agent-settings'] });
    },
    onError: (e) => notifications.show({ color: 'red', message: (e as Error).message }),
  });

  const calibration = useQuery({
    queryKey: ['calibration'],
    queryFn: () => unwrap(draftCalibration({ headers: org })),
  });
  const misses = useQuery({
    queryKey: ['misses'],
    queryFn: () => unwrap(draftMisses({ headers: org, query: { days: 30, limit: 20 } })),
  });

  if (settings.isLoading) return <Loader size="sm" />;

  return (
    <Stack>
      <Card withBorder padding="md">
        <Stack>
          <Text fw={600}>Who it answers as</Text>
          <TextInput
            label="Business name"
            value={business}
            onChange={(e) => setBusiness(e.currentTarget.value)}
          />
          <Textarea
            label="House rules"
            description="Tone, sign-off, anything the team always says. Added after the agent's own rules — it cannot be told to invent a price."
            autosize
            minRows={3}
            value={instructions}
            onChange={(e) => setInstructions(e.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button loading={savePersona.isPending} onClick={() => savePersona.mutate()}>
              Save
            </Button>
          </Group>
        </Stack>
      </Card>

      <Card withBorder padding="md">
        <Stack>
          <Group justify="space-between">
            <Text fw={600}>Send without a person</Text>
            <Switch
              checked={enabled}
              onChange={(e) => {
                setEnabled(e.currentTarget.checked);
              }}
            />
          </Group>
          <Text size="sm" c="dimmed">
            Off, every draft waits for somebody. On, a draft still has to clear four more gates: the
            agent did not ask for a person, its confidence reaches the threshold, its intent is
            allowed, and nobody has claimed the thread.
          </Text>
          <NumberInput
            label="Confidence threshold"
            min={0}
            max={1}
            step={0.05}
            decimalScale={2}
            value={threshold}
            onChange={setThreshold}
            disabled={!enabled}
          />
          <MultiSelect
            label="Intents it may send"
            description={
              intents.length === 0
                ? 'None chosen means every intent may go out unattended.'
                : 'Only these go out unattended; everything else waits for a person.'
            }
            placeholder={intents.length === 0 ? 'All intents' : undefined}
            data={INTENTS}
            value={intents}
            onChange={(v) => setIntents(v as Intent[])}
            clearable
            disabled={!enabled}
          />
          <Group justify="flex-end">
            <Button loading={saveDial.isPending} onClick={() => saveDial.mutate()}>
              Save
            </Button>
          </Group>
        </Stack>
      </Card>

      <Card withBorder padding="md">
        <Stack>
          <Text fw={600}>Where to put the dial</Text>
          <Text size="sm" c="dimmed">
            What became of the drafts in each confidence band. A band where somebody had to fix most
            drafts is a band the dial should not reach.
          </Text>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Confidence</Table.Th>
                <Table.Th>Drafts</Table.Th>
                <Table.Th>Sent as-is</Table.Th>
                <Table.Th>Fixed first</Table.Th>
                <Table.Th>Discarded</Table.Th>
                <Table.Th>By the dial</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(calibration.data?.buckets ?? []).map((b) => (
                <Table.Tr key={b.bucket}>
                  <Table.Td>
                    {percent(b.bucket / 10)}–{percent(b.bucket / 10 + 0.1)}
                  </Table.Td>
                  <Table.Td>{b.total}</Table.Td>
                  <Table.Td>{b.sentAsIs}</Table.Td>
                  <Table.Td>{b.sentEdited}</Table.Td>
                  <Table.Td>{b.discarded}</Table.Td>
                  <Table.Td>{b.autoSent}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          {(calibration.data?.buckets ?? []).length === 0 ? (
            <Text size="sm" c="dimmed">
              Nothing drafted yet.
            </Text>
          ) : null}
        </Stack>
      </Card>

      <Card withBorder padding="md">
        <Stack gap="xs">
          <Text fw={600}>What it got wrong</Text>
          <Text size="sm" c="dimmed">
            Drafts somebody rewrote or threw away. Each one is either a house rule to add above, or
            a knowledge document that is missing.
          </Text>
          {(misses.data?.misses ?? []).map((m) => (
            <Card key={m.draftId} withBorder padding="xs" radius="sm">
              <Group justify="space-between">
                <Text size="sm" fw={500} lineClamp={1}>
                  {m.subject}
                </Text>
                <Badge size="xs" color={m.kind === 'discarded' ? 'red' : 'orange'} variant="light">
                  {m.kind}
                </Badge>
              </Group>
              <Text size="xs" c="dimmed" lineClamp={2}>
                It said: {m.body}
              </Text>
              {m.sentBody ? (
                <Text size="xs" lineClamp={2}>
                  Sent instead: {m.sentBody}
                </Text>
              ) : null}
              <Text size="xs" c="dimmed">
                {when(m.at)} · {percent(m.confidence)} confident
              </Text>
            </Card>
          ))}
          {(misses.data?.misses ?? []).length === 0 ? (
            <Text size="sm" c="dimmed">
              Nothing to learn from yet.
            </Text>
          ) : null}
        </Stack>
      </Card>
    </Stack>
  );
}

function KnowledgeTab() {
  const org = useOrg();
  const qc = useQueryClient();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');

  const docs = useQuery({
    queryKey: ['knowledge'],
    queryFn: () => unwrap(listKnowledge({ headers: org })),
  });
  const add = useMutation({
    mutationFn: () => unwrap(addKnowledge({ headers: org, body: { title, content } })),
    onSuccess: () => {
      setTitle('');
      setContent('');
      void qc.invalidateQueries({ queryKey: ['knowledge'] });
    },
    onError: (e) => notifications.show({ color: 'red', message: (e as Error).message }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => unwrap(deleteKnowledge({ path: { id }, headers: org })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['knowledge'] }),
  });

  return (
    <Stack>
      <Card withBorder padding="md">
        <Stack>
          <Text fw={600}>Add something the agent may use</Text>
          <Text size="sm" c="dimmed">
            Indexed as it is saved, so the next redraft can already cite it.
          </Text>
          <TextInput label="Title" value={title} onChange={(e) => setTitle(e.currentTarget.value)} />
          <Textarea
            label="Text"
            autosize
            minRows={5}
            value={content}
            onChange={(e) => setContent(e.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button
              disabled={!title.trim() || !content.trim()}
              loading={add.isPending}
              onClick={() => add.mutate()}
            >
              Save
            </Button>
          </Group>
        </Stack>
      </Card>

      {(docs.data?.documents ?? []).map((d) => (
        <Card key={d.id} withBorder padding="sm">
          <Group justify="space-between">
            <Group gap="xs">
              <Text fw={500}>{d.title}</Text>
              <Badge
                size="xs"
                variant="light"
                color={d.status === 'indexed' ? 'green' : d.status === 'failed' ? 'red' : 'gray'}
              >
                {d.status}
              </Badge>
              <Text size="xs" c="dimmed">
                {d.chunkCount} pieces
              </Text>
            </Group>
            <Button
              size="compact-xs"
              variant="subtle"
              color="red"
              onClick={() => remove.mutate(d.id)}
            >
              Remove
            </Button>
          </Group>
          {d.error ? (
            <Alert color="red" variant="light" mt="xs" p="xs">
              {d.error}
            </Alert>
          ) : null}
        </Card>
      ))}
    </Stack>
  );
}

function ChannelsTab() {
  const org = useOrg();
  const qc = useQueryClient();
  const [form, setForm] = useState({
    imapHost: '',
    imapUsername: '',
    smtpHost: '',
    fromAddress: '',
    secret: '',
  });

  const accounts = useQuery({
    queryKey: ['channel-accounts'],
    queryFn: () => unwrap(listChannelAccounts({ headers: org })),
  });
  const connect = useMutation({
    mutationFn: () => unwrap(createChannelAccount({ headers: org, body: { kind: 'email', ...form } })),
    onSuccess: () => {
      setForm({ imapHost: '', imapUsername: '', smtpHost: '', fromAddress: '', secret: '' });
      void qc.invalidateQueries({ queryKey: ['channel-accounts'] });
    },
    onError: (e) => notifications.show({ color: 'red', message: (e as Error).message }),
  });
  const toggle = useMutation({
    mutationFn: (v: { id: string; enabled: boolean }) =>
      unwrap(setChannelAccountEnabled({ path: { id: v.id }, headers: org, body: { enabled: v.enabled } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['channel-accounts'] }),
  });

  return (
    <Stack>
      {(accounts.data?.accounts ?? []).map((a) => (
        <Card key={a.id} withBorder padding="sm">
          <Group justify="space-between">
            <div>
              <Group gap="xs">
                <Text fw={500}>{a.label || a.fromAddress || a.botUsername || a.imapUsername}</Text>
                <Badge size="xs" variant="default">
                  {a.kind}
                </Badge>
                {!a.hasSecret ? (
                  <Badge size="xs" color="red" variant="light">
                    no password stored
                  </Badge>
                ) : null}
              </Group>
              {a.webhookToken ? (
                <Text size="xs" c="dimmed">
                  Webhook path: /api/webhooks/telegram/{a.webhookToken}
                </Text>
              ) : null}
            </div>
            <Switch
              checked={a.enabled}
              onChange={(e) => toggle.mutate({ id: a.id, enabled: e.currentTarget.checked })}
            />
          </Group>
        </Card>
      ))}

      <Card withBorder padding="md">
        <Stack>
          <Text fw={600}>Connect a mailbox</Text>
          <Text size="sm" c="dimmed">
            The password is sealed before it is stored and never comes back out.
          </Text>
          <TextInput
            label="IMAP host"
            value={form.imapHost}
            onChange={(e) => setForm({ ...form, imapHost: e.currentTarget.value })}
          />
          <TextInput
            label="Mailbox"
            value={form.imapUsername}
            onChange={(e) => setForm({ ...form, imapUsername: e.currentTarget.value })}
          />
          <TextInput
            label="SMTP host"
            value={form.smtpHost}
            onChange={(e) => setForm({ ...form, smtpHost: e.currentTarget.value })}
          />
          <TextInput
            label="Reply from"
            value={form.fromAddress}
            onChange={(e) => setForm({ ...form, fromAddress: e.currentTarget.value })}
          />
          <TextInput
            label="Password"
            type="password"
            value={form.secret}
            onChange={(e) => setForm({ ...form, secret: e.currentTarget.value })}
          />
          <Group justify="flex-end">
            <Button
              disabled={!form.imapHost || !form.imapUsername}
              loading={connect.isPending}
              onClick={() => connect.mutate()}
            >
              Connect
            </Button>
          </Group>
        </Stack>
      </Card>
    </Stack>
  );
}
